import {action, observable, computed, runInAction} from 'mobx';
import {EventEmitter} from 'events';
import {ITreeNode} from '@techsee/techsee-ui-common/lib/simple-tree-control-view';
import get from 'lodash/get';
import forEach from 'lodash/forEach';
import findIndex from 'lodash/findIndex';
import filter from 'lodash/filter';
import sortBy from 'lodash/sortBy';
import last from 'lodash/last';
import {IResourcesService} from '@techsee/techsee-client-infra/lib/services/ResourcesServices';
import {getRootStore} from '../../../../../app.bootstrap';
import {requireImage} from '../../../../../utils/utils';

export enum LibraryDialogEvents {
    CLOSE = 'CLOSE',
    SELECT_ITEM = 'SELECT_ITEM',
    SEND_ITEM = 'SEND_ITEM',
    SYNC_SELECTION = 'SYNC_SELECTION'
}

export interface CarouselItem {
    active: boolean;
    data: ITreeNode;
}

export class LibraryDialogController extends EventEmitter {
    @observable isVisible: boolean = false;
    @observable items: ITreeNode[] = [];
    @observable carouselItems: CarouselItem[] = [];
    @observable libDirectories: string[] = [];
    @observable selectedFileIndex: number = 0;
    @observable activeType: string = '';
    private _collection: ITreeNode | null = null;
    private _topLevelDir: string = '';
    private _footer: boolean = false;
    private _offlineMode: boolean = false;
    private _endMeeting: boolean = false;
    private readonly resourceService: IResourcesService;

    public readonly translate: (key: string) => string;
    public readonly requireImage: typeof requireImage;

    constructor(resourceService: IResourcesService, offlineMode: boolean, endMeeting: boolean) {
        super();
        this.resourceService = resourceService;
        this._offlineMode = offlineMode;
        this._endMeeting = endMeeting;
        this.translate = getRootStore().localizationService.translate;
        this.requireImage = requireImage;
    }

    @computed get selectedNode(): ITreeNode | null {
        return this.findSelectedNode(this.items);
    }

    @computed get showFooter(): boolean {
        return this._footer && this.carouselItems.length > 0;
    }

    private findSelectedNode(items: ITreeNode[]): ITreeNode | null {
        for (const item of items) {
            if (item.state?.selected) {
                return item;
            }
        }
        return null;
    }

    @computed get currentCarouselIndex(): number {
        return this.carouselItems.findIndex((i) => i.active);
    }

    @computed get canAnnotate(): boolean {
        return this.activeType === 'img';
    }

    @computed get canSend(): boolean {
        return !this._offlineMode && !this._endMeeting;
    }

    @computed get offlineMode(): boolean {
        return this._offlineMode;
    }

    @action
    setEndMeeting = (endMeeting: boolean): void => {
        this._endMeeting = endMeeting;
    };

    @action
    open = (tree: ITreeNode, selectedNode: ITreeNode, topLevel: string, footer: boolean): void => {
        this._collection = tree;
        this._topLevelDir = topLevel;
        this._footer = footer;

        // Don't show dialog yet - wait for data to load
        // Flatten parents array (convert ObservableArray to regular array)
        const parents = selectedNode.parents ? selectedNode.parents.slice() : [];
        this.libDirectories = [topLevel].concat(parents);

        if (selectedNode.data.type === 'dir') {
            // Opening a directory
            this.loadDirectory(selectedNode.data.title, selectedNode);
        } else {
            // Opening with a file selected
            const lastDir = last(this.libDirectories) || topLevel;
            this.loadDirectory(lastDir, selectedNode);
        }
    };

    @action
    close = (): void => {
        this.isVisible = false;
        this.items = [];
        this.carouselItems = [];
        this.libDirectories = [];
        this.emit(LibraryDialogEvents.CLOSE);
    };

    @action
    loadDirectory = (dirNode: string, selectedNode?: ITreeNode): void => {
        if (!this._collection) {
            return;
        }

        // Determine if we're selecting a file (not a directory)
        const fileSelection =
            selectedNode && selectedNode.data && selectedNode.data.type !== 'dir' ? selectedNode : undefined;

        // Handle top-level directory
        if (dirNode === this._topLevelDir) {
            console.log('[LibraryDialog] Loading top level');
            this._setItems(
                this._collection.children || [],
                () => {
                    this.libDirectories = [dirNode];
                    this._loadCarousel(fileSelection);
                },
                fileSelection
            );
            return;
        }

        // Navigate to the target directory
        let targetCollection = this._collection;

        // Walk through the tree using the breadcrumb path
        for (const directory of this.libDirectories.slice(1)) {
            if (!targetCollection.children) break;
            const childIndex = findIndex(targetCollection.children, (child) => child.data?.title === directory);
            if (childIndex >= 0) {
                targetCollection = targetCollection.children[childIndex];
            }
            // Stop if we've reached the target directory
            if (directory === dirNode) {
                break;
            }
        }

        // If we have a selectedNode (clicked item), use it as the target
        if (selectedNode && selectedNode.data.type === 'dir') {
            targetCollection = selectedNode;
        }

        // Load the directory contents
        if (targetCollection && targetCollection.children) {
            this._setItems(
                targetCollection.children,
                () => {
                    // Update breadcrumbs
                    const parents = targetCollection.parents ? targetCollection.parents.slice() : [];
                    this.libDirectories = [this._topLevelDir].concat(parents).concat([dirNode]);
                    // Load carousel with file selection
                    this._loadCarousel(fileSelection);
                },
                fileSelection
            );
        }
    };

    @action
    clickItem = (item: ITreeNode): void => {
        if (item.data.type === 'dir') {
            // Navigate into directory
            this.loadDirectory(item.data.title, item);
        } else if (this._footer) {
            // With footer: select item in carousel and update selection state
            runInAction(() => {
                // Find the item in items array
                const itemIndex = findIndex(this.items, (i) => i && i.data && i.data.url === item.data.url);

                if (itemIndex >= 0) {
                    // Create a new array with updated selection state to trigger MobX reactivity
                    this.items = this.items.map((i, index) => {
                        if (i.state) {
                            i.state.selected = index === itemIndex;
                        }
                        return i;
                    });

                    this._setActiveCarouselItem(this.items[itemIndex]);

                    // Emit event to sync with LibraryTree
                    this.emit(LibraryDialogEvents.SYNC_SELECTION, this.items[itemIndex]);
                }
            });
        } else {
            // Without footer: select item and close
            this._updateSecureItem({data: item.data} as any).then((updatedItem) => {
                this.emit(LibraryDialogEvents.SELECT_ITEM, updatedItem);
                this.close();
            });
        }
    };

    @action
    annotateItem = (): void => {
        if (!this.canAnnotate) return;

        const item = this.carouselItems.find((i) => i.active);
        if (item) {
            this._updateSecureItem(item.data).then((updatedItem) => {
                this.emit(LibraryDialogEvents.SELECT_ITEM, updatedItem);
                this.close();
            });
        }
    };

    @action
    sendItem = (): void => {
        if (!this.canSend) return;

        const item = this.carouselItems.find((i) => i.active);
        if (item) {
            const dataWithSend = {...item.data, send: true};
            this._updateSecureItem(dataWithSend).then((updatedItem) => {
                this.emit(LibraryDialogEvents.SEND_ITEM, updatedItem);
                this.close();
            });
        }
    };

    @action
    setCarouselActive = (index: number): void => {
        runInAction(() => {
            // Update carousel active state
            forEach(this.carouselItems, (item, i) => {
                item.active = i === index;
            });

            if (index >= 0 && this.carouselItems[index]) {
                // Find the corresponding item in items
                const selectedItemUrl = this.carouselItems[index].data.data.url;
                const selectedItemIndex = findIndex(this.items, (item) => item.data.url === selectedItemUrl);

                if (selectedItemIndex >= 0) {
                    const selectedItem = this.items[selectedItemIndex];

                    // Create a new array with updated selection state to trigger MobX reactivity
                    this.items = this.items.map((item, index) => {
                        if (item.state) {
                            item.state.selected = index === selectedItemIndex;
                        }
                        return item;
                    });

                    this.selectedFileIndex = selectedItemIndex;
                    this.activeType = selectedItem.data.type;

                    // Emit event to sync with LibraryTree
                    this.emit(LibraryDialogEvents.SYNC_SELECTION, selectedItem);
                }
            }
        });
    };

    @action
    private _setItems(items: ITreeNode[], cb: () => void, selectedNode?: ITreeNode): void {
        // Filter items that have data property and a resource with _id (need resign for signed URLs)
        const itemsWithResources: ITreeNode[] = filter(
            items,
            (item) => item && item.data && get(item, 'data.resource._id')
        ) as ITreeNode[];

        const promises = itemsWithResources.map((item) =>
            this.resourceService
                .resign(item.data.resource._id)
                .then((resp: any) => {
                    const data = get(resp, 'data', resp);
                    if (item && item.data && data) {
                        item.data.resource = data;
                        if (data.url) {
                            item.data.url = data.url;
                        }
                    }
                })
                .catch((err) => {
                    console.error('[LibraryDialog] Error resigning resource:', err);
                })
        );

        Promise.all(promises).then(() => {
            // Only include items that have data property
            const validItems = filter(items, (item) => item && item.data) as ITreeNode[];

            // All state modifications must be wrapped in runInAction
            runInAction(() => {
                // Unselect all items first
                forEach(validItems, (item) => {
                    if (item.state) {
                        item.state.selected = false;
                    }
                });

                // Mark selected node
                if (selectedNode) {
                    const foundItem = validItems.find(
                        (item) => item.data.url === selectedNode.data.url || item.data.title === selectedNode.data.title
                    );
                    if (foundItem && foundItem.state) {
                        foundItem.state.selected = true;
                    }
                } else {
                    // No selection provided - select first non-directory item
                    const firstNonDirItem = validItems.find((item) => item.data.type !== 'dir');
                    if (firstNonDirItem && firstNonDirItem.state) {
                        firstNonDirItem.state.selected = true;
                    }
                }

                // Sort: selected first, then by type
                const sortedItems = sortBy(validItems, [
                    (item) => (item.state?.selected ? 0 : 1), // Selected items first
                    'data.type' // Then by type
                ]);

                // Set items first, then execute callback
                this.items = sortedItems;
                cb();

                // Open dialog after data is loaded
                this.isVisible = true;
            });
        });
    }

    private _updateSecureItem(item: ITreeNode): Promise<ITreeNode> {
        if (!item || !item.data) {
            return Promise.resolve(item);
        }

        if (get(item, 'data.resource.security.enabled')) {
            return this.resourceService
                .resign(item.data.resource._id)
                .then((resp: any) => {
                    if (item.data && resp && resp.data) {
                        item.data.resource = resp.data;
                        if (resp.data.url) {
                            item.data.url = resp.data.url;
                        }
                    }
                    return item;
                })
                .catch((err) => {
                    console.error('[LibraryDialog] Error updating secure item:', err);
                    return item;
                });
        }

        return Promise.resolve(item);
    }

    @action
    private _loadCarousel(selectedNode?: ITreeNode): void {
        this.carouselItems = [];
        let hasActiveItem = false;

        forEach(this.items, (item, index) => {
            // Skip items without data or url (directories don't have URLs)
            if (!item || !item.data || !item.data.url) {
                return;
            }

            // Item is active if it's marked as selected in state
            const active = !!item.state?.selected;
            if (active) {
                this.selectedFileIndex = index;
                hasActiveItem = true;
            }

            this.carouselItems.push({
                active,
                data: item
            });
        });

        // If no item is active but we have items, make the first one active
        if (this.carouselItems.length > 0 && !hasActiveItem) {
            this.carouselItems[0].active = true;

            // Find the index of this item in items array
            this.selectedFileIndex = findIndex(this.items, (i) => i.data.url === this.carouselItems[0].data.data.url);

            // Also mark the item as selected in state
            if (this.selectedFileIndex >= 0 && this.items[this.selectedFileIndex].state) {
                this.items[this.selectedFileIndex].state.selected = true;
            }
        }

        if (this.carouselItems.length > 0 && this.selectedFileIndex >= 0) {
            const selectedItem = this.items[this.selectedFileIndex];
            if (selectedItem && selectedItem.data) {
                this.activeType = selectedItem.data.type;
            }
        }
    }

    @action
    private _setActiveCarouselItem(item: ITreeNode): void {
        if (!item || !item.data) return;

        // Find the index in items array
        const foundIndex = findIndex(this.items, (i) => i && i.data && i.data.url === item.data.url);
        this.selectedFileIndex = foundIndex >= 0 ? foundIndex : 0;

        // Update carousel items - set only the clicked item as active
        forEach(this.carouselItems, (cItem) => {
            cItem.active = cItem.data && cItem.data.data && cItem.data.data.url === item.data.url;
        });

        this.activeType = item.data.type;
    }

    dispose(): void {
        this.removeAllListeners();
    }
}
